import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react"

const BLOCK_TAGS = ["DIV", "P", "LI"]
const TOKEN      = /\{\{\s*(.+?)\s*\}\}/g
const ESCAPED    = { "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }

const escape = text => String(text).replace(/[&<>"']/g, character => ESCAPED[character])

const pillHtml = (token, labels, removeLabel) => {
  const expression = token.replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "")
  return `<span class="token-pill" contenteditable="false" data-token="${escape(token)}">${escape(labels[expression] || expression)}` +
         `<button type="button" class="token-pill__remove" tabindex="-1" title="${escape(removeLabel)}">&times;</button></span>`
}

const pillify = (text, labels, removeLabel) => {
  let html = ""
  let read = 0
  let match
  TOKEN.lastIndex = 0
  while ((match = TOKEN.exec(text)) !== null) {
    html += escape(text.slice(read, match.index)) + pillHtml(match[0], labels, removeLabel)
    read = TOKEN.lastIndex
  }
  return html + escape(text.slice(read))
}

const serialize = node => {
  let text = ""
  node.childNodes.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) text += child.nodeValue
    else if (child.nodeType !== Node.ELEMENT_NODE) return
    else if (child.classList.contains("token-pill")) text += child.dataset.token
    else if (child.tagName === "BR") text += "\n"
    else if (BLOCK_TAGS.includes(child.tagName)) {
      if (text && !text.endsWith("\n")) text += "\n"
      text += serialize(child)
    } else text += serialize(child)
  })
  return text
}

// The text is read from the DOM, never written back from state: React re-rendering it would lose the caret.
const PillEditor = forwardRef(({ initialValue, labels, removeLabel, className, onChange, onFocus }, ref) => {
  const editor = useRef(null)

  useEffect(() => { editor.current.innerHTML = pillify(initialValue || "", labels, removeLabel) }, [])

  const sync = () => onChange(serialize(editor.current))

  useImperativeHandle(ref, () => ({
    insert(token) {
      const holder = document.createElement("div")
      holder.innerHTML = pillHtml(token, labels, removeLabel)
      const nodes = [holder.firstChild, document.createTextNode(" ")]

      const selection = window.getSelection()
      let range
      if (selection.rangeCount && editor.current.contains(selection.anchorNode)) {
        range = selection.getRangeAt(0)
        range.deleteContents()
      } else {
        range = document.createRange()
        range.selectNodeContents(editor.current)
        range.collapse(false)
      }
      nodes.forEach(node => { range.insertNode(node); range.setStartAfter(node) })
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
      editor.current.focus()
      sync()
    }
  }))

  const handleClick = event => {
    const button = event.target.closest(".token-pill__remove")
    if (!button) return
    button.closest(".token-pill").remove()
    sync()
  }

  return (
    <div ref={editor} contentEditable suppressContentEditableWarning className={`interpolated-editor ${className}`}
         onInput={sync} onClick={handleClick} onFocus={onFocus} />
  )
})

export default PillEditor
